// Per-account scoping of local persistence. Each document library (models,
// layouts, bakes) and the sync outbox lives in a NAMESPACED IndexedDB database:
// the legacy database names hold the ANONYMOUS pool (what you see signed out),
// and each account gets its own `<base>.acct.<userId>` databases. Signing in
// swaps the active namespace, so one account never sees another's documents —
// and signing out clears the slate back to the anonymous pool instead of
// leaving the account's library readable by whoever uses the browser next.
//
// This module is deliberately auth-agnostic (it holds a string, not a session):
// auth/session.ts drives setActiveScope from Supabase auth changes, and main
// reacts to onScopeChange by resetting the open documents + re-listing the dock.

import { type IDBStore, createIDBStore } from "../model/idb-store";

// The scope documents live in before any sign-in — keeps the legacy database
// names, so pre-account local data simply becomes the anonymous pool.
export const ANONYMOUS_SCOPE = "";

let active = ANONYMOUS_SCOPE;
const listeners = new Set<(scope: string) => void>();

export function activeScope(): string {
  return active;
}

// `null`/`""` → anonymous. Notifies subscribers only on an actual change.
export function setActiveScope(userId: string | null): void {
  const next = userId ?? ANONYMOUS_SCOPE;
  if (next === active) return;
  active = next;
  for (const cb of listeners) cb(next);
}

export function onScopeChange(cb: (scope: string) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function scopedDbName(base: string, scope: string): string {
  return scope === ANONYMOUS_SCOPE ? base : `${base}.acct.${scope}`;
}

// Every base database name that gets per-scope copies, recorded at
// createScopedStore time so account deletion can wipe a scope completely.
const scopedBases = new Set<string>();

// Delete every local database belonging to `scope` — the departed account's
// document libraries and outbox. Never touches the anonymous pool. Best-effort:
// resolves even if a database is still blocked (stores close themselves on
// versionchange, so in practice deletion proceeds). No-op outside the browser.
export async function wipeScopeLocalData(scope: string): Promise<void> {
  if (scope === ANONYMOUS_SCOPE || typeof indexedDB === "undefined") return;
  await Promise.all(
    [...scopedBases].map(
      (base) =>
        new Promise<void>((res) => {
          const req = indexedDB.deleteDatabase(scopedDbName(base, scope));
          req.onsuccess = req.onerror = req.onblocked = () => res();
        }),
    ),
  );
}

// An IDBStore whose every call routes to the ACTIVE scope's database, plus an
// escape hatch to address a specific scope (the sync controller pins its pull/
// claim/flush operations to the scope captured at connect, so a sign-out
// mid-operation can't leak writes into the wrong pool).
export interface ScopedStore<T extends { id: string; updatedAt: number }> extends IDBStore<T> {
  forScope(scope: string): IDBStore<T>;
}

export function isScopedStore<T extends { id: string; updatedAt: number }>(
  store: IDBStore<T>,
): store is ScopedStore<T> {
  return typeof (store as ScopedStore<T>).forScope === "function";
}

// Resolve a store to a fixed scope; plain (unscoped) stores — the in-memory
// fakes tests inject — are returned as-is.
export function storeForScope<T extends { id: string; updatedAt: number }>(
  store: IDBStore<T>,
  scope: string,
): IDBStore<T> {
  return isScopedStore(store) ? store.forScope(scope) : store;
}

// `factory` is injectable so tests run against in-memory stores keyed by the
// database name; production opens one real IndexedDB database per scope, on
// demand, cached for the session.
export function createScopedStore<T extends { id: string; updatedAt: number }>(
  baseDbName: string,
  storeName: string,
  factory: (dbName: string, storeName: string) => IDBStore<T> = createIDBStore,
): ScopedStore<T> {
  scopedBases.add(baseDbName);
  const perScope = new Map<string, IDBStore<T>>();
  function forScope(scope: string): IDBStore<T> {
    let s = perScope.get(scope);
    if (!s) {
      s = factory(scopedDbName(baseDbName, scope), storeName);
      perScope.set(scope, s);
    }
    return s;
  }
  // Route per CALL (not per closure) so a store handed out before a sign-in
  // still reads/writes the current account's pool afterwards.
  return {
    list: () => forScope(active).list(),
    listKeys: () => forScope(active).listKeys(),
    get: (id) => forScope(active).get(id),
    put: (item) => forScope(active).put(item),
    remove: (id) => forScope(active).remove(id),
    forScope,
  };
}
